import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Stack,
    Box,
    Typography,
    Alert,
    AlertTitle,
    Button,
    useMediaQuery
} from '@mui/material';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AtplPrimaryBt from "../../components/ATPlab/AtplPrimaryBtn/AtplPrimaryBt";
import AtplFooter from '../../components/ATPlab/atpFooter/AtplFooter';
import AtplLinearBar from "../../components/ATPlab/AtplLinearBar";
import FaceScanInstruction from '../../components/faceScanInstruction/faceScanInstruction';
import AtplPrimaryBtnNew from '../../components/ATPlab/AtplPrimaryBtn/AtplPrimaryBtnNew';
import { ATPLBackButton } from '../../components/ATPlab/AtplBackBtn';

export const AtplPreScanScreen = () => {
    const navigate = useNavigate();
    const [ error, setError ] = useState( null );
    const { isFaceScanFailed } = location.state || {};
    const [ currentStep, setCurrentStep ] = useState( 0 )
    const isSmallHeight = useMediaQuery( '(max-height:700px)' );
    useEffect( () => {
        if ( !isFaceScanFailed ) {
            try {
                const redirectUrl = localStorage.getItem( 'atpl-redirect-url' );
                if ( !redirectUrl ) {
                    setError( 'Missing redirect URL. Please complete the demographics form first.' );
                    navigate( '/atpl-demographic' );
                }
            } catch ( err ) {
                setError( 'Failed to access local storage. Please try again.' );
            }
        }
    }, [ navigate, isFaceScanFailed ] );

    const redirectToFaceScan = async () => {
        try {
            const redirectUrl = localStorage.getItem( 'atpl-redirect-url' );
            if ( redirectUrl ) {
                window.localStorage.setItem( 'from_atp', 'true' );
                window.location.href = redirectUrl;
            } else {
                throw new Error( 'Redirect URL not found' );
            }
        } catch ( err ) {
            setError( 'Failed to proceed. Please try again.' );
        }
    };

    const onBackArrowClickHandler = () => {
        // if page is 0 and bak arrow click navigate to demographics page
        if ( currentStep === 0 ) {
            try {
                navigate( '/atpl-demographics', { state: { visitAsBack: true } } );
            } catch ( err ) {
                setError( 'Failed to navigate back. Please try again.' );
            }
        } else {
            setCurrentStep( 0 )
        }

    };

    const handleNextBtnOnBrightnessSreen = () => {
        redirectToFaceScan()
    }

    const handleDismissError = () => {
        setError( null );
    };

    const handleHomeClick = () => {
        navigate( '/atpllanding' );
    };

    const handleNextOnFirstScreen = () => {
        setCurrentStep( currentStep + 1 )
    }


    const renderStepContent = () => {
        switch ( currentStep ) {
            case 0:
                return renderFirstStep();
            case 1:
                return renderSecondStep();
            default:
                return null;
        }
    };

    // Render the first step - introduction screen
    function renderFirstStep() {
        return (
            <Box sx={ {
                width: { xs: '100%', sm: '100%', md: '100%' },
                maxWidth: { xs: '300px', sm: '390px', md: '600px' },
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 0, sm: 3, md: 4 },
                justifyContent: 'center',
                alignItems : "center"
            } }>
                <Typography sx={ {
                    fontSize: { xs: isSmallHeight ? '20px' : '22px', sm: '25px', md: '30px' },
                    fontWeight: 700,
                    lineHeight: 1.2,
                    textAlign: 'center',
                    color: '#30373E',
                    mb: 2
                } }>
                    Now, we will scan your face to measure your HRV.
                </Typography>
                <Typography sx={ {
                    fontSize: { xs: '14px', sm: '15px', md: '15px' },
                    fontWeight: 400,
                    lineHeight: { xs: '18px', sm: '22px', md: "24px" },
                    textAlign: 'center',
                    color: '#000000',
                    mb: 2
                } }>
                    Heart rate variability (HRV) is related to stress, aging and other metrics of physical and mental health.
                </Typography>
                <Typography sx={ {
                    fontSize: { xs: '14px', sm: '15px', md: '15px' },
                    fontWeight: 400,
                    lineHeight: { xs: '18px', sm: '22px', md: "24px" },
                    textAlign: 'center',
                    color: '#000000',
                    mb: 2
                } }>
                    Based on these results, we can curate product recommendations <br /> that will work specifically for you.
                </Typography>
                <Typography sx={ {
                    fontSize: { xs: '14px', sm: '15px', md: '15px' },
                    fontWeight: 400,
                    lineHeight: { xs: '18px', sm: '22px', md: "24px" },
                    textAlign: 'center',
                    color: '#000000',
                    mb: isSmallHeight ? 0 : 0
                } }>
                    💡 Please make sure you are in a brightly lit area and the light is spread evenly on your face. Remove your hat and eyeglasses and stay close to the camera and still during the 30-second scan to get a good measurement.
                </Typography>
                <Box sx={ {
                    display: 'flex',
                    justifyContent: 'center',
                    mt: isSmallHeight ? 'auto' : 0,
                    mb: isSmallHeight ? 0 : 'auto',
                    maxWidth: "342px",
                    width : "100%"
                     
                } }>
                    <AtplPrimaryBtnNew 
                        text='Next'
                        onClick={ handleNextOnFirstScreen }
                        sx={ {
                            // minWidth: { xs: '140px', sm: '180px', md: '200px' },
                            marginTop: { xs: '18px', sm: '30px', md: '20px', lg: "30px" },
                            width : "100%",
                        } }
                    />
                </Box>
            </Box>
        );
    }

    // Render the second step - face scan instructions
    function renderSecondStep() {
        return (
            <Box sx={ {
                width: '100%',
                maxWidth: { xs: '300px', sm: '320px', md: '600px' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            } }>
                <FaceScanInstruction
                    videoContainerSx={ {
                        marginY: { xs: isSmallHeight ? "8px" : "12px", sm: "16px", md: "20px" }
                    } }
                />

                <Box sx={ {
                    display: 'flex',
                    justifyContent: 'center',
                    mt: isSmallHeight ? 2 : 3,
                    maxWidth: "342px",
                    width : "100%",
                } }>
                    <AtplPrimaryBtnNew
                        text="Let's go!"
                        onClick={ handleNextBtnOnBrightnessSreen }
                        sx={ {
                            maxWidth: "342px",
                            width : "100%",
                            minWidth: { xs: '140px', sm: '180px', md: '200px' },
                            marginTop: { xs: '4px', sm: '30px', md: '20px', lg: "20px" },
                        } }
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={ {
            width: '100%',
            height: '100vh',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            justifyContent: 'center',
            alignItems: 'center',
            boxSizing : "border-box",
            width : "100%"
        } }>
            {/* Error Alert */ }
            { error && (
                <Box sx={ {
                    position: 'fixed',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '90%',
                    maxWidth: '600px',
                    zIndex: 1000
                } }>
                    <Alert
                        severity="error"
                        variant="filled"
                        onClose={ handleDismissError }
                        sx={ { mb: 2 } }
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={ handleHomeClick }
                                sx={ {
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        textDecoration: 'none'
                                    }
                                } }
                            >
                                Return Home
                            </Button>
                        }
                    >
                        <AlertTitle>Error</AlertTitle>
                        { error }
                    </Alert>
                </Box>
            ) }

            <Box sx={ {
                maxWidth: { xs: '100%', sm: '390px', md: '1200px' },
                width: '100%',
                height: '100%',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',

                position: 'relative',
                px: { xs: 2, sm: 4, md: 4 },
                boxSizing : "border-box"
            } }>
                {/* Header with back button and progress bar */ }
                <Box sx={ { py: 2 } }>
                    <Stack textAlign="start">
                        <ATPLBackButton onClick={ onBackArrowClickHandler } />
                    </Stack>
                    <AtplLinearBar value={ 21 } />
                </Box>

                {/* Main content area */ }
                <Box sx={ {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    width : "100%"
                } }>
                    { renderStepContent() }
                </Box>
                {/* Footer */ }
                <Box sx={ {
                    mt: 'auto',
                    pb: { xs: 2, sm: 3, md: 4 },
                    width: '100%'
                } }>
                    <AtplFooter footerTitle="This health assessment is powered by" />
                </Box>
            </Box>
        </Box>
    );
};